import { useCallback, useEffect } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import {
	getMeProfileGetMeProfileQueryKey,
	useMeProfileGetMeProfile,
	useMeProfileUpdateMeProfile,
} from '@cocoonspace/api'
import { Form } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as z from 'zod/v4'

import { toProfileUpdatePayload } from './profile-update-payload'
import { isProfileSlugAvailable } from './reserved-profile-slugs'

export type GeneralInfoFormData = z.infer<typeof schema>

export const profileLinkTypes = [
	'website',
	'instagram',
	'linkedin',
	'facebook',
	'youtube',
	'tiktok',
	'x',
] as const

const schema = z.object({
	// The preview of the avatar, used to display the avatar in the form
	avatarPreview: z.string().optional(),
	bannerPreview: z.string().optional(),
	logoPreview: z.string().optional(),
	backgroundPreview: z.string().optional(),
	avatarUrl: z.string().optional(),
	styles: z.object({
		bannerImageUrl: z.string().optional(),
		backgroundImageUrl: z.string().optional(),
		logoUrl: z.string().optional(),
		cssVariables: z.record(z.string(), z.string()).optional(),
	}),
	title: z.string(),
	subtitle: z.string(),
	slug: z.string().min(1).refine(isProfileSlugAvailable),
	description: z.string(),
	externalLinks: z.array(
		z.object({
			type: z.string().min(1),
			url: z.url().refine((url) => /^https?:\/\//i.test(url)),
		}),
	),
	eventTypeIds: z.array(z.string()).optional(),
})

export const useProfileForm = ({ onSuccess }: { onSuccess?: () => void }) => {
	const { t } = useLingui()
	const queryClient = useQueryClient()

	const { mutateAsync: updateMeProfile, isPending } =
		useMeProfileUpdateMeProfile()

	const { data: meProfile, isLoading } = useMeProfileGetMeProfile({
		query: { retry: false },
	})

	const form = useForm<GeneralInfoFormData>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			avatarPreview: undefined,
			bannerPreview: undefined,
			logoPreview: undefined,
			backgroundPreview: undefined,
			avatarUrl: '',
			styles: {},
			title: undefined,
			subtitle: '',
			slug: undefined,
			description: undefined,
			externalLinks: [],
			eventTypeIds: undefined,
		},
	})

	useEffect(() => {
		if (!meProfile || !('title' in meProfile)) return

		form.reset({
			avatarPreview: meProfile.avatarUrl ?? undefined,
			bannerPreview: meProfile.styles?.bannerImageUrl ?? undefined,
			logoPreview: meProfile.styles?.logoUrl ?? undefined,
			backgroundPreview: meProfile.styles?.backgroundImageUrl ?? undefined,
			avatarUrl: meProfile.avatarUrl ?? '',
			styles: {
				bannerImageUrl: meProfile.styles?.bannerImageUrl ?? '',
				backgroundImageUrl: meProfile.styles?.backgroundImageUrl ?? '',
				logoUrl: meProfile.styles?.logoUrl ?? '',
				cssVariables: meProfile.styles?.cssVariables,
			},
			title: meProfile.title ?? undefined,
			subtitle: meProfile.subtitle ?? '',
			slug: meProfile.slug ?? undefined,
			description: meProfile.description ?? undefined,
			externalLinks: meProfile.externalLinks ?? [],
			eventTypeIds: meProfile.eventTypeIds ?? undefined,
		})
	}, [form, meProfile])

	const onSubmit = useCallback(
		async (data: GeneralInfoFormData) => {
			const payload = toProfileUpdatePayload(data)

			const query = updateMeProfile({ data: payload }).then(async () => {
				await queryClient.invalidateQueries({
					queryKey: getMeProfileGetMeProfileQueryKey(),
				})
				onSuccess?.()
			})

			toast.promise(query, {
				loading: t`Mise à jour en cours...`,
				success: t`Profil mis à jour`,
				error: t`Une erreur est survenue lors de la mise à jour du profil`,
			})
		},
		[updateMeProfile, queryClient, t, onSuccess],
	)

	return {
		form,
		isLoading,
		isPending,
		onSubmit,
	}
}

export const useProfileFormContext = () => useFormContext<GeneralInfoFormData>()

export const UserProfileFormWrapper = ({
	children,
	className,
	onSuccess,
}: {
	children: React.ReactNode
	className?: string
	onSuccess?: () => void
}) => {
	const { form, onSubmit, isLoading, isPending } = useProfileForm({
		onSuccess,
	})

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className={className}
			>
				<fieldset disabled={isLoading || isPending}>{children}</fieldset>
			</form>
		</Form>
	)
}
